import { type ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';

/**
 * Two-pane master/detail layout shell for Admin Module tabs.
 *
 * Left pane (master) renders a filterable, scrollable list. Right pane
 * (detail) renders the form for the selected row or the empty state.
 * The layout is responsive: above the `lg` breakpoint the two panes
 * sit side by side; below it the detail pane slides in over the list
 * (handled by the parent setting `mobileShowDetail`).
 *
 * Surfaces use the shadcn semantic tokens (`bg-card`, `border-border`,
 * `text-foreground`) so the shell follows the theme. Shared by the
 * Entities tab today and the Users / Roles tabs next.
 */
export interface AdminMasterDetailProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly toolbar?: ReactNode;
  readonly list: ReactNode;
  readonly detail: ReactNode;
  /**
   * When true, the right-hand detail pane occupies the full width on
   * narrow viewports (mobile master/detail "drill-down" pattern). When
   * false, the list pane stays visible. Parent tab owns this state so
   * the back-button affordance can be wired to its row selection.
   */
  readonly mobileShowDetail?: boolean;
  readonly onMobileBack?: () => void;
}

const PANE = 'overflow-hidden rounded-lg border border-border bg-card shadow-sm min-h-0';

export function AdminMasterDetail({
  title,
  subtitle,
  toolbar,
  list,
  detail,
  mobileShowDetail = false,
  onMobileBack,
}: AdminMasterDetailProps) {
  return (
    <div className="flex h-full flex-col">
      <PageHeader title={title} subtitle={subtitle} toolbar={toolbar} />

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
        <div
          className={cn(PANE, mobileShowDetail ? 'hidden lg:flex lg:flex-col' : 'flex flex-col')}
        >
          {list}
        </div>
        <div
          className={cn(PANE, mobileShowDetail ? 'flex flex-col' : 'hidden lg:flex lg:flex-col')}
        >
          {mobileShowDetail && onMobileBack && (
            <button
              type="button"
              onClick={onMobileBack}
              className="flex items-center gap-1.5 border-b border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:bg-muted lg:hidden"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Back to list
            </button>
          )}
          {detail}
        </div>
      </div>
    </div>
  );
}
